import { z } from 'zod/v4';

import { getNormalizedFileName } from '../export/exportFileName';
import { assertSuccessfulResponseGenericErrorCode } from '../http/assertSuccessfulResponseGenericErrorCode';
import { endpoint } from '../http/endpoint';
import { PcbDesignIdRuntype, PcbIdRuntype } from '../idTypes';
import { FileTypeRuntype, FileTypeUpdateRequestRuntype, UploadFileResponseRuntype } from './pcbBackendTypes';

export const pcbFilesEndpoints = {
    'PUT /assembly/assemblies/:assembly/versions/:version/fileapproval/status': endpoint({
        description:
            'Locks or unlocks the file matching of the assembly version. Locking is what re-triggers PCB processing: it re-runs outline, layerstack and customer-panel, and transitively reconciliation, the specification renders, analysis, production-analysis and dfm wherever their prerequisites are green. To retry a board that stalled on infrastructure rather than data, send {locked: false} and then {locked: true}. Both the :version path param and the ?initial flag are accepted and ignored — the route always acts on the current version. Locking fails with unapprovedFiles while any file is still unmatched.',
        pathParams: z.object({ assembly: PcbDesignIdRuntype, version: PcbIdRuntype }),
        queryParams: z.undefined(),
        requestBody: z.object({ locked: z.boolean() }),
        responseBody: z.null(),
        handleResponse: async (response, _) => {
            if (response.ok) {
                return null;
            }
            throw Error(`Failed to set the file approval state: ${response.status}`);
        },
        invalidates: [
            'GET /assembly/assemblies/:assembly/lifecycles',
            'GET /assembly/assemblies/:assembly/versions/:version/lifecycles',
            'GET /assembly/assemblies/:assembly/versions/:version/lifecyclesummary',
            'GET /ems/pcb/v2/pcbs/:pcbId',
        ],
        actionPermission: () => 'CanEditPcb',
    }),

    'PUT /assembly/assemblies/:assembly/versions/:version/files': endpoint({
        description:
            'Locks the file matching of the assembly version, re-triggering PCB processing. Equivalent to the fileapproval/status route with {locked: true}, and it always locks — use fileapproval/status when you need to unlock. Sent as a manual action, so it also re-locks a version that a user unlocked by hand.',
        pathParams: z.object({ assembly: PcbDesignIdRuntype, version: PcbIdRuntype }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.null(),
        handleResponse: async (response, _) => {
            if (response.ok) {
                return null;
            }
            throw Error(`Failed to approve the file matches: ${response.status}`);
        },
        invalidates: [
            'GET /assembly/assemblies/:assembly/lifecycles',
            'GET /assembly/assemblies/:assembly/versions/:version/lifecycles',
            'GET /assembly/assemblies/:assembly/versions/:version/lifecyclesummary',
            'GET /ems/pcb/v2/pcbs/:pcbId',
        ],
        actionPermission: () => 'CanEditPcb',
    }),

    'POST /files/ems/pcb/domatch': endpoint({
        description:
            'Dry run of the automatic file matcher: returns the file type it would assign to each uploaded file, keyed by filename, and mutates nothing. Useful before committing an assignment via PUT /pcb/:pcbId/files, which performs no server-side validation of its own. Send the files as multipart form data.',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.instanceof(FormData),
        responseBody: z.record(z.string(), FileTypeRuntype),
        actionPermission: () => 'CanViewPcb',
    }),

    'POST /ems/pcb/pcbs/:pcbId/files': endpoint({
        description:
            'Uploads the initial set of files for a PCB. Send them as multipart form data keyed by file name. Rejects with 406 when a file format is not supported and 413 when a file is too large; both are expected outcomes a user can recover from. Served under the /files prefix rather than /api, which is where the Scala route lives.',
        base: 'files',
        pathParams: z.object({ pcbId: PcbIdRuntype }),
        queryParams: z.undefined(),
        requestBody: z.instanceof(FormData),
        responseBody: z.array(z.object({ name: z.string(), fType: FileTypeRuntype })),
        invalidates: [
            'GET /ems/pcb/v2/pcbs/:pcbId',
            'GET /assembly/assemblies/:assembly/lifecycles',
            'GET /assembly/assemblies/:assembly/versions/:version/lifecycles',
            'GET /assembly/assemblies/:assembly/versions/:version/lifecyclesummary',
        ],
        actionPermission: () => 'CanEditPcb',
    }),

    'POST /pcb/:pcbId/upload-files': endpoint({
        description:
            'Adds files to a PCB that already has files, unlocking the version first. Send them as multipart form data under the empty-string field name — the handler matches that name literally, and files sent under any other name are silently dropped with a 200. The other upload route cannot add to an existing file set, so without this a partial first upload can only be undone by deleting the PCB.',
        pathParams: z.object({ pcbId: PcbIdRuntype }),
        queryParams: z.undefined(),
        requestBody: z.instanceof(FormData),
        responseBody: UploadFileResponseRuntype,
        invalidates: [
            'GET /ems/pcb/v2/pcbs/:pcbId',
            'GET /assembly/assemblies/:assembly/lifecycles',
            'GET /assembly/assemblies/:assembly/versions/:version/lifecycles',
            'GET /assembly/assemblies/:assembly/versions/:version/lifecyclesummary',
        ],
    }),

    'PUT /pcb/:pcbId/files': endpoint({
        description:
            'Updates the file type of one or more PCB files, unlocking the version first. `file` is the file name, not the file id. One unknown name rejects the whole batch.',
        pathParams: z.object({ pcbId: PcbIdRuntype }),
        requestBody: FileTypeUpdateRequestRuntype,
        queryParams: z.undefined(),
        responseBody: z.literal('OK'),
        invalidates: ['GET /ems/pcb/v2/pcbs/:pcbId', 'GET /custom-part-alerts/:partId'],
    }),

    'DELETE /pcb/:pcbId/files': endpoint({
        description: 'Delete pcb file',
        pathParams: z.object({ pcbId: PcbIdRuntype }),
        requestBody: z.object({
            files: z.array(z.string()),
        }),
        queryParams: z.undefined(),
        responseBody: z.literal('OK'),
        invalidates: ['GET /ems/pcb/v2/pcbs/:pcbId'],
    }),

    'GET /ems/pcb/v2/pcbs/:pcbId/files/all': endpoint({
        description:
            'Downloads every file of the PCB as a zip. Serves the uploaded archive directly when it is a single zip, otherwise packs the originals.',
        pathParams: z.object({ pcbId: PcbIdRuntype }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({ fileName: z.string(), blob: z.instanceof(Blob) }),
        handleResponse: async (response, endpoint) => {
            await assertSuccessfulResponseGenericErrorCode(response, endpoint);
            return { fileName: getNormalizedFileName(response), blob: await response.blob() };
        },
        actionPermission: () => 'CanViewPcb',
    }),

    'GET /ems/pcb/v2/pcbs/:pcbId/files/singular/:fileName': endpoint({
        description:
            'Downloads a single named file of the PCB. Kept separate from the /files/all route rather than taking one `:filePath` param, because a combined param would percent-encode the slash and match neither route.',
        pathParams: z.object({ pcbId: PcbIdRuntype, fileName: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({ fileName: z.string(), blob: z.instanceof(Blob) }),
        handleResponse: async (response, endpoint) => {
            await assertSuccessfulResponseGenericErrorCode(response, endpoint);
            return { fileName: getNormalizedFileName(response), blob: await response.blob() };
        },
        actionPermission: () => 'CanViewPcb',
    }),

    'GET /ems/renderer/assemblies/:assemblyId/versions/:pcbId/files/:fileName': endpoint({
        description: 'Get pcb file svg',
        pathParams: z.object({
            assemblyId: PcbDesignIdRuntype,
            pcbId: PcbIdRuntype,
            fileName: z.string(),
        }),
        requestBody: z.undefined(),
        queryParams: z.object({
            format: z.literal('svg'),
        }),
        responseBody: z.string().nullable(),
        handleResponse: async (response, _) => {
            if (response.ok) return response.text();
            if (response.status === 404) return null;
            throw Error(`Unexpected response status ${response.status}`);
        },
    }),
};
